import React, {
  useEffect,
  useState,
  ChangeEvent,
  SetStateAction,
  Dispatch,
  useReducer,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { FilesDropdownMenu } from "src/app/metafields/FileField/FilesDropdown";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import { ChangeWillAffectManyRowsModal } from "src/app/modals/ChangeWillAffectManyRowsModal/ChangeWillAffectManyRowsModal";
import { showToast } from "src/app/methods/showToast";
import {
  deleteFileBasingOnContext,
  uploadFileBasingOnContext,
} from "src/app/metafields/FileField/taskFunctions";
import { getGlobalTasks } from "src/redux/creator-database/creatorDatabaseActions";
import { MetaFileData } from "src/app/metafields/MultipleFileField/MultipleFileField";
import { Rectangle } from "@glideapps/glide-data-grid";
import { RootState } from "src/redux/reducers";
import { MetaDataFileValue, Row } from "src/redux/dictionary/types";
import { tableDataType } from "src/app/components/Table/Table";
import { useParams } from "react-router";
import { getCellPosition, initialStaticCellSettings } from "../../utils";
import { GlideDataGridContext, StaticCellSettings } from "../../types";
import creatorDatabaseFiltersReducer, {
  creatorDatabaseFiltersReducerInitialState,
} from "../../../../CreatorDatabase/redux/creatorDatabaseFiltersReducer";
import { getDictionaryListElement } from "../../../../../redux";

interface FilesDropdownWrapperProps {
  filesData: MetaDataFileValue[];
  uuid: string;
  rowUuid: string;
  readOnly: boolean;
  closeDropdown: () => void;
  setStaticCellSettings: Dispatch<SetStateAction<StaticCellSettings>>;
  bounds: Rectangle;
  context: GlideDataGridContext;
  selectedRowsData?: Row[];
  fieldName?: string;
  isUniqueField?: boolean;
}

function FilesDropdownWrapper({
  setStaticCellSettings,
  filesData,
  uuid,
  rowUuid,
  readOnly,
  closeDropdown,
  bounds,
  context,
  selectedRowsData,
  fieldName,
  isUniqueField,
}: FilesDropdownWrapperProps) {
  const [localFilesData, setLocalFilesData] = useState<MetaFileData[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    assetId: string;
    assetName: string;
  }>({ assetId: "", assetName: "" });
  const [pendingUploadEvent, setPendingUploadEvent] =
    useState<ChangeEvent<HTMLInputElement> | null>(null);
  const params = useParams<{ dictionaryUuid: string }>();

  const dispatch = useDispatch();
  const {
    creatorDatabaseReducer: { globalTaskColumns },
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const [creatorDatabaseFilters] = useReducer(
    creatorDatabaseFiltersReducer,
    creatorDatabaseFiltersReducerInitialState(globalTaskColumns),
  );
  const { filters, sortBy, sort } = creatorDatabaseFilters;

  const isGlobalTask = context === tableDataType.CreatorDatabase;

  const uploadToSingleRow = async (
    e: ChangeEvent<HTMLInputElement>,
    targetRowUuid: string,
    onLocalFilesChange?: Dispatch<SetStateAction<MetaFileData[]>>,
  ) => {
    await uploadFileBasingOnContext({
      e,
      wsMetaFieldUuid: uuid,
      wsWorkspaceUuid: activeWorkspaceUuid,
      setFilesData: onLocalFilesChange,
      wsTaskUuid: targetRowUuid,
      thenCallback: () => {
        if (context === tableDataType.Dictionary) {
          dispatch(
            getDictionaryListElement(params.dictionaryUuid, targetRowUuid),
          );
        }
      },
      context: isGlobalTask ? tableDataType.GlobalTask : context,
    });
  };

  const uploadToMultipleRows = async (e: ChangeEvent<HTMLInputElement>) => {
    setStaticCellSettings({
      numberOfFiles: e.target.files?.length ?? 0,
      style: getCellPosition(bounds),
    });
    try {
      for (const row of selectedRowsData || []) {
        // Only the row this dropdown belongs to owns `localFilesData`;
        // other selected rows pick up the new file once the grid refetches.
        const onLocalFilesChange =
          row.uuid === rowUuid ? setLocalFilesData : undefined;
        // eslint-disable-next-line no-await-in-loop
        await uploadToSingleRow(e, row.uuid, onLocalFilesChange);
      }
      if (isGlobalTask) {
        dispatch(
          getGlobalTasks(activeWorkspaceUuid, { filters, sortBy, sort }),
        );
      }
    } finally {
      closeDropdown();
      setStaticCellSettings(initialStaticCellSettings);
    }
  };

  const uploadFile = (e: ChangeEvent<HTMLInputElement>) => {
    const isPartOfMultiSelection =
      (selectedRowsData?.length || 0) > 1 &&
      selectedRowsData?.some((row) => row.uuid === rowUuid);

    if (isPartOfMultiSelection) {
      if (context === tableDataType.Dictionary && isUniqueField) {
        showToast(
          "error",
          "Error",
          "This field must be unique and can't be bulk-updated for multiple rows.",
        );
        return;
      }
      setPendingUploadEvent(e);
      return;
    }

    closeDropdown();
    setStaticCellSettings({
      numberOfFiles: e.target.files?.length ?? 0,
      style: getCellPosition(bounds),
    });
    uploadFileBasingOnContext({
      e,
      wsMetaFieldUuid: uuid,
      wsWorkspaceUuid: activeWorkspaceUuid,
      setFilesData: setLocalFilesData,
      wsTaskUuid: rowUuid,
      thenCallback: () => {
        if (isGlobalTask) {
          dispatch(
            getGlobalTasks(activeWorkspaceUuid, { filters, sortBy, sort }),
          );
        }

        if (context === tableDataType.Dictionary) {
          dispatch(getDictionaryListElement(params.dictionaryUuid, rowUuid));
        }
      },
      finallyCallback: () => {
        setStaticCellSettings(initialStaticCellSettings);
      },
      context: isGlobalTask ? tableDataType.GlobalTask : context,
    });
  };

  const confirmMultiUpload = () => {
    const event = pendingUploadEvent;
    setPendingUploadEvent(null);
    if (event) {
      uploadToMultipleRows(event);
    }
  };

  const cancelMultiUpload = () => {
    setPendingUploadEvent(null);
    closeDropdown();
  };

  const deleteFile = (assetId: string) => {
    deleteFileBasingOnContext({
      assetId,
      taskId: rowUuid,
      fieldId: uuid,
      thenCallback: () => {
        setLocalFilesData((prev) => {
          const newFilesList = prev.filter((file) => file.assetId !== assetId);
          if (!newFilesList.length) {
            closeDropdown();
          }
          return newFilesList;
        });
        if (isGlobalTask) {
          dispatch(
            getGlobalTasks(activeWorkspaceUuid, { filters, sortBy, sort }),
          );
        }

        if (context === tableDataType.Dictionary) {
          dispatch(getDictionaryListElement(params.dictionaryUuid, rowUuid));
        }
      },
      context: isGlobalTask ? tableDataType.GlobalTask : context,
    });
  };

  useEffect(() => {
    setLocalFilesData(filesData);
  }, [filesData]);

  return (
    <>
      <FilesDropdownMenu
        filesData={localFilesData}
        uploadFile={uploadFile}
        readOnly={readOnly}
        onRequestDelete={(assetId, assetName) => {
          setFileToDelete({ assetId, assetName });
          setShowConfirmationModal(true);
        }}
      />

      {showConfirmationModal && (
        <RemoveModal
          onClose={() => setShowConfirmationModal(false)}
          objectNames={[fileToDelete?.assetName ?? ""]}
          removeFunction={() => {
            setShowConfirmationModal(false);
            deleteFile(fileToDelete.assetId);
          }}
        />
      )}

      {pendingUploadEvent && (
        <ChangeWillAffectManyRowsModal
          onClose={cancelMultiUpload}
          fieldName={fieldName || ""}
          rowsNumber={selectedRowsData?.length || 0}
          onConfirmClick={confirmMultiUpload}
          onCancelClick={cancelMultiUpload}
        />
      )}
    </>
  );
}

export default FilesDropdownWrapper;
